import { CombinedTimeSeriesOverview } from 'components/TimeSeriesOverview';
import { INSTAGRAM_COUNTRIES } from '@/config/constants';
import { POPULARITY_METADATA_BY_KEY } from 'utils/metrics';
import PopularityTimeSeries from 'components/DataVis/Charts/PopularityTimeSeries';
import TikTokTimeSeriesNoDataMessage from 'components/TikTokTimeSeriesNoDataMessage';
import { useEntityPageContext } from './EntityContext';

/** One line per source the index is stitched from, plus the index itself. */
export const popularitySeries = ({ page }) => {
    const { entry, theme } = page;
    const out = [];
    const push = (timeSeries, id, color) =>
        timeSeries && out.push({ id, timeSeries, color });
    push(entry?.timeSeriesGoogle, 'google', theme.google_blue);
    push(entry?.timeSeriesTikTok, 'tiktok', theme.tiktok_pink);
    push(entry?.timeSeriesInstagram, 'instagram', theme.instagram_orange);
    push(entry?.timeSeriesOverview, 'overview', theme.purple);
    return out;
};

const group = (keys, label, dated = true) => ({ keys, label, dated });
const COMBINED = group(['YoY', 'QoQ', 'MoM'], 'combined');
// The search group alone carries no week or year.
const PER_SOURCE = [
    group(['search_YoY', 'search_QoQ', 'search_MoM'], null, false),
    group(['tiktok_YoY', 'tiktok_QoQ', 'tiktok_MoM'], null),
];
const INSTAGRAM = group(
    ['instagram_YoY', 'instagram_QoQ', 'instagram_MoM'],
    null,
);
const PREDICTION = group(['predicted_YoY'], null, false);

const metricsFor = (page, groups) =>
    page.row
        ? groups.map(({ keys, label, dated }) => {
              const metricsList = keys.map(
                  (k) => POPULARITY_METADATA_BY_KEY[k],
              );
              return {
                  metrics: page.row || {},
                  metricsList,
                  defaultMetric: metricsList[0],
                  ...(label && { metricsListLabel: label }),
                  ...(dated ? { week: page.week, year: page.year } : {}),
              };
          })
        : null;

/**
 * The popularity chart. `combined` and `prediction` choose which metric groups
 * sit under it, which is why they are read here rather than passed down.
 */
const PopularityEntityChart = ({
    data,
    combined = true,
    prediction = false,
    ...rest
}) => {
    const page = useEntityPageContext();
    const { isLoading, name, country, entityId, entityType } = page;

    return (
        <PopularityTimeSeries
            data={data}
            showPlaceholder={
                isLoading ||
                data.every(
                    ({ timeSeries }) => !(timeSeries && timeSeries.length),
                )
            }
            placeholderMessage={
                isLoading ? 'Loading...' : <TikTokTimeSeriesNoDataMessage />
            }
            children={null}
            chartHeight={250}
            showKey={false}
            name={name}
            country={country?.id}
            {...(entityType === 'brands'
                ? { brand: entityId }
                : { category: entityId })}
            driversEnabled={true}
            {...rest}
        >
            <CombinedTimeSeriesOverview
                isLoading={isLoading}
                metrics={metricsFor(page, [
                    ...(combined ? [COMBINED] : []),
                    ...PER_SOURCE,
                    ...(country && INSTAGRAM_COUNTRIES.includes(country.text_id)
                        ? [INSTAGRAM]
                        : []),
                ])}
                {...(prediction && {
                    predictionMetrics: metricsFor(page, [PREDICTION]),
                })}
            />
        </PopularityTimeSeries>
    );
};

export default PopularityEntityChart;
